const createSlotRange = (startHour, startMinute, endHour, endMinute) => {
  const slots = [];
  let cursor = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;

  while (cursor <= end) {
    const hour = Math.floor(cursor / 60);
    const minute = cursor % 60;
    slots.push(
      `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
    );
    cursor += 15;
  }

  return slots;
};

const createSplitSlotSet = (
  morningStartHour,
  morningStartMinute,
  morningEndHour,
  morningEndMinute,
  afternoonStartHour,
  afternoonStartMinute,
  afternoonEndHour,
  afternoonEndMinute
) => {
  const morningSlots =
    morningStartHour === null
      ? []
      : createSlotRange(
          morningStartHour,
          morningStartMinute,
          morningEndHour,
          morningEndMinute
        );

  const afternoonSlots =
    afternoonStartHour === null
      ? []
      : createSlotRange(
          afternoonStartHour,
          afternoonStartMinute,
          afternoonEndHour,
          afternoonEndMinute
        );

  return {
    morning: morningSlots,
    afternoon: afternoonSlots,
  };
};

const SLOT_SETS = {
  HMDN_WEEKDAY: () => createSplitSlotSet(6, 30, 12, 0, 13, 0, 16, 30),
  HMDN_SUNDAY: () => createSplitSlotSet(7, 30, 11, 30, null, null, null, null),
  HMSG_WEEKDAY: () => createSplitSlotSet(7, 30, 11, 30, 12, 0, 16, 0),
  HMSG_SUNDAY: () => createSplitSlotSet(7, 30, 11, 30, 12, 0, 16, 0),
  HMTD_WEEKDAY: () => createSplitSlotSet(7, 0, 11, 30, 12, 30, 16, 0),
  HMTD_SUNDAY: () => createSplitSlotSet(7, 0, 11, 30, null, null, null, null),
  HMDNG_ALL: () => createSplitSlotSet(7, 0, 11, 30, 12, 0, 16, 0),
  HMDL_WEEKDAY: () => createSplitSlotSet(7, 0, 11, 30, 12, 0, 16, 0),
  HMDL_SUNDAY: () => createSplitSlotSet(7, 30, 11, 30, null, null, null, null),
  HMGKD_WEEKDAY: () => createSplitSlotSet(7, 30, 11, 30, 12, 0, 17, 0),
  HMGPXL_WEEKDAY: () => createSplitSlotSet(7, 0, 11, 30, 12, 0, 16, 0),
  HMCL_SUNDAY: () => createSplitSlotSet(6, 0, 11, 0, null, null, null, null),
};

const cloneRule = (rule) => JSON.parse(JSON.stringify(rule));

const BOOKING_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const BASE_HOSPITAL_RULES = {
  hmdn: {
    settings: {
      allowSameDay: false,
      // Offsets from today: Saturday cannot book Sun+Mon; Sunday cannot book Mon.
      skipLeadDays: {
        sat: [1, 2],
        sun: [1],
      },
      bookingCondition: [
        // Thứ 2 - Thứ 6: sau 16h30 chỉ cho đặt buổi chiều ngày hôm sau.
        {
          days: ["mon", "tue", "wed", "thu", "fri"],
          time: 1630,
          distance: -1,
        },
      ],
    },
    slots: {
      mon: SLOT_SETS.HMDN_WEEKDAY(),
      tue: SLOT_SETS.HMDN_WEEKDAY(),
      wed: SLOT_SETS.HMDN_WEEKDAY(),
      thu: SLOT_SETS.HMDN_WEEKDAY(),
      fri: SLOT_SETS.HMDN_WEEKDAY(),
      sat: SLOT_SETS.HMDN_WEEKDAY(),
      sun: SLOT_SETS.HMDN_SUNDAY(),
    },
  },
  hmsg: {
    settings: {
      allowSameDay: false,
      excludedSlots: ["11:30", "12:00", "12:15", "16:00"],
      // First N picker days (from first rendered day per allowSameDay) are selectable; later days stay visible but disabled; omit/0 = no cap.
      maxSelectableBookingDays: 2,
    },
    slots: {
      mon: SLOT_SETS.HMSG_WEEKDAY(),
      tue: SLOT_SETS.HMSG_WEEKDAY(),
      wed: SLOT_SETS.HMSG_WEEKDAY(),
      thu: SLOT_SETS.HMSG_WEEKDAY(),
      fri: SLOT_SETS.HMSG_WEEKDAY(),
      sat: SLOT_SETS.HMSG_WEEKDAY(),
      sun: SLOT_SETS.HMSG_SUNDAY(),
    },
  },
  hmtd: {
    settings: {
      allowSameDay: true,
      bookingCondition: [
        // distance -1 nghĩa là slot vào chiều ngày hôm sau mới hiện ra
        {
          days: ["mon", "tue", "wed", "thu", "fri", "sat"],
          time: 1500,
          distance: -1,
        },
        // distance 4 nghĩa là slot cách đó ít nhất 4 tiếng mới hiện ra
        {
          days: ["mon", "tue", "wed", "thu", "fri", "sat"],
          time: 0,
          distance: 4,
        },
        // Chủ nhật: nghỉ -> distance -2 nghĩa là không hiển thị slot nào
        {
          days: ["sun"],
          time: 0,
          distance: -2,
        },
      ],
    },
    slots: {
      mon: SLOT_SETS.HMTD_WEEKDAY(),
      tue: SLOT_SETS.HMTD_WEEKDAY(),
      wed: SLOT_SETS.HMTD_WEEKDAY(),
      thu: SLOT_SETS.HMTD_WEEKDAY(),
      fri: SLOT_SETS.HMTD_WEEKDAY(),
      sat: SLOT_SETS.HMTD_WEEKDAY(),
      sun: SLOT_SETS.HMTD_SUNDAY(),
    },
  },
  hmdng: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmcl: {
    settings: {
      allowSameDay: false,
      bookingCondition: [
        // distance 24 nghĩa là slot cách đó ít nhất 24 tiếng mới hiện ra
        // Ví dụ: đặt lúc 20:00 hôm nay thì ngày mai cũng không có slot nào vì chưa cách đủ 24h
        {
          days: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
          time: 0,
          distance: 24,
        },
      ],
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMCL_SUNDAY(),
    },
  },
  hmv: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmdl: {
    settings: {
      allowSameDay: false,
    },
    slots: {
      mon: SLOT_SETS.HMDL_WEEKDAY(),
      tue: SLOT_SETS.HMDL_WEEKDAY(),
      wed: SLOT_SETS.HMDL_WEEKDAY(),
      thu: SLOT_SETS.HMDL_WEEKDAY(),
      fri: SLOT_SETS.HMDL_WEEKDAY(),
      sat: SLOT_SETS.HMDL_WEEKDAY(),
      sun: SLOT_SETS.HMDL_SUNDAY(),
    },
  },
  hmbd: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmbp: {
    settings: {
      allowSameDay: false,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmmh: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmvp: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMDNG_ALL(),
      tue: SLOT_SETS.HMDNG_ALL(),
      wed: SLOT_SETS.HMDNG_ALL(),
      thu: SLOT_SETS.HMDNG_ALL(),
      fri: SLOT_SETS.HMDNG_ALL(),
      sat: SLOT_SETS.HMDNG_ALL(),
      sun: SLOT_SETS.HMDNG_ALL(),
    },
  },
  hmgkd: {
    settings: {
      allowSameDay: true,
    },
    slots: {
      mon: SLOT_SETS.HMGKD_WEEKDAY(),
      tue: SLOT_SETS.HMGKD_WEEKDAY(),
      wed: SLOT_SETS.HMGKD_WEEKDAY(),
      thu: SLOT_SETS.HMGKD_WEEKDAY(),
      fri: SLOT_SETS.HMGKD_WEEKDAY(),
      sat: SLOT_SETS.HMGKD_WEEKDAY(),
      sun: { morning: [], afternoon: [] },
    },
  },
  hmgpxl: {
    settings: {
      allowSameDay: false,
    },
    slots: {
      mon: SLOT_SETS.HMGPXL_WEEKDAY(),
      tue: SLOT_SETS.HMGPXL_WEEKDAY(),
      wed: SLOT_SETS.HMGPXL_WEEKDAY(),
      thu: SLOT_SETS.HMGPXL_WEEKDAY(),
      fri: SLOT_SETS.HMGPXL_WEEKDAY(),
      sat: SLOT_SETS.HMGPXL_WEEKDAY(),
      sun: { morning: [], afternoon: [] },
    },
  },
};

const HOSPITAL_BOOKING_RULES = {
  danang: cloneRule(BASE_HOSPITAL_RULES.hmdng),
  "da-nang": cloneRule(BASE_HOSPITAL_RULES.hmdng),
  thuduc: cloneRule(BASE_HOSPITAL_RULES.hmtd),
  "thu-duc": cloneRule(BASE_HOSPITAL_RULES.hmtd),
  saigon: cloneRule(BASE_HOSPITAL_RULES.hmsg),
  "sai-gon": cloneRule(BASE_HOSPITAL_RULES.hmsg),
  cuulong: cloneRule(BASE_HOSPITAL_RULES.hmcl),
  "cuu-long": cloneRule(BASE_HOSPITAL_RULES.hmcl),
  dongnai: cloneRule(BASE_HOSPITAL_RULES.hmdn),
  "dong-nai": cloneRule(BASE_HOSPITAL_RULES.hmdn),
  vinh: cloneRule(BASE_HOSPITAL_RULES.hmv),
  "vinh-hospital": cloneRule(BASE_HOSPITAL_RULES.hmv),
  dalat: cloneRule(BASE_HOSPITAL_RULES.hmdl),
  "da-lat": cloneRule(BASE_HOSPITAL_RULES.hmdl),
  binhduong: cloneRule(BASE_HOSPITAL_RULES.hmbd),
  "binh-duong": cloneRule(BASE_HOSPITAL_RULES.hmbd),
  minhhai: cloneRule(BASE_HOSPITAL_RULES.hmmh),
  "minh-hai": cloneRule(BASE_HOSPITAL_RULES.hmmh),
  binhphuoc: cloneRule(BASE_HOSPITAL_RULES.hmbp),
  "binh-phuoc": cloneRule(BASE_HOSPITAL_RULES.hmbp),
  vanphuc2: cloneRule(BASE_HOSPITAL_RULES.hmvp),
  "van-phuc-2": cloneRule(BASE_HOSPITAL_RULES.hmvp),
  "gold-pxl": cloneRule(BASE_HOSPITAL_RULES.hmgpxl),
  goldpxl: cloneRule(BASE_HOSPITAL_RULES.hmgpxl),
  "hoanmy-gold-ky-dong": cloneRule(BASE_HOSPITAL_RULES.hmgkd),
  "hoan-my-gold-ky-dong": cloneRule(BASE_HOSPITAL_RULES.hmgkd),
};

const getHospitalBookingSettings = (hospitalKey) => {
  if (!hospitalKey) {
    return {};
  }
  const key = String(hospitalKey).trim().toLowerCase();
  return HOSPITAL_BOOKING_RULES[key]?.settings || {};
};

const getSkipLeadOffsets = (settings, now = new Date()) => {
  const todayKey = BOOKING_DAY_KEYS[now.getDay()];
  const raw = settings?.skipLeadDays?.[todayKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 0);
};

const getMinBookingDate = (hospitalKey, now = new Date()) => {
  const settings = getHospitalBookingSettings(hospitalKey);
  const today = startOfDay(now);
  const skipSet = new Set(getSkipLeadOffsets(settings, now));
  const allowSameDay =
    typeof settings.allowSameDay === "boolean" ? settings.allowSameDay : true;

  for (let offset = 0; offset < 14; offset += 1) {
    if (!allowSameDay && offset === 0) {
      continue;
    }
    if (skipSet.has(offset)) {
      continue;
    }
    const candidate = new Date(today);
    candidate.setDate(today.getDate() + offset);
    return candidate;
  }

  const fallback = new Date(today);
  fallback.setDate(today.getDate() + (allowSameDay ? 0 : 1));
  return fallback;
};

const isBookingDateAllowed = (hospitalKey, appointmentDate, now = new Date()) => {
  if (!(appointmentDate instanceof Date) || Number.isNaN(appointmentDate.getTime())) {
    return false;
  }

  const today = startOfDay(now);
  const appointment = startOfDay(appointmentDate);
  if (appointment.getTime() < today.getTime()) {
    return false;
  }

  const settings = getHospitalBookingSettings(hospitalKey);
  const offset = Math.round((appointment.getTime() - today.getTime()) / MS_PER_DAY);
  if (settings.allowSameDay === false && offset === 0) {
    return false;
  }

  return !getSkipLeadOffsets(settings, now).includes(offset);
};

const CRM_PICKER_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const CRM_PICKER_DEFAULT_MAX_DAYS_AHEAD = 90;

const startOfLocalDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addLocalDays = (date, days) => {
  const d = startOfLocalDay(date);
  d.setDate(d.getDate() + days);
  return d;
};

const parseConditionTimeToMinutes = (value) => {
  const normalized = String(value || "0").padStart(4, "0");
  const hour = parseInt(normalized.slice(0, 2), 10);
  const minute = parseInt(normalized.slice(2), 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return 0;
  }

  return hour * 60 + minute;
};

const formatDisabledDateYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}/${m}/${d}`;
};

/**
 * Date-picker bounds for the CRM modal only (min/max/disabled weekdays/dates).
 * Does not filter CRM slot times — legacy slot modal keeps its own engine.
 */
const getCrmDatePickerConstraints = (hospitalKey, now = new Date()) => {
  const today = startOfLocalDay(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const todayDayKey = CRM_PICKER_DAY_KEYS[now.getDay()];

  const normalizedKey =
    typeof hospitalKey === "string" && hospitalKey.trim().length > 0
      ? hospitalKey.trim().toLowerCase()
      : null;

  const hospitalConfig =
    normalizedKey && HOSPITAL_BOOKING_RULES[normalizedKey]
      ? HOSPITAL_BOOKING_RULES[normalizedKey]
      : null;
  const settings = hospitalConfig?.settings || {};
  const bookingConditions = Array.isArray(settings.bookingCondition)
    ? settings.bookingCondition
    : [];

  let minDate = getMinBookingDate(normalizedKey, now);

  // Lead time >= 24h: first selectable calendar day is day-of (now + 24h).
  for (const condition of bookingConditions) {
    if (
      !condition ||
      !Array.isArray(condition.days) ||
      !condition.days.includes(todayDayKey)
    ) {
      continue;
    }

    const distance = Number(condition.distance);
    if (Number.isNaN(distance) || distance < 24) {
      continue;
    }

    const threshold = parseConditionTimeToMinutes(condition.time);
    if (threshold !== 0 && currentMinutes < threshold) {
      continue;
    }

    const dayOfPlusLead = startOfLocalDay(
      new Date(now.getTime() + distance * 60 * 60 * 1000),
    );
    if (dayOfPlusLead.getTime() > minDate.getTime()) {
      minDate = dayOfPlusLead;
    }
  }

  const disabledWeekDays = [];
  for (const condition of bookingConditions) {
    if (!condition || Number(condition.distance) !== -2) {
      continue;
    }
    if (!Array.isArray(condition.days)) {
      continue;
    }
    for (const dayKey of condition.days) {
      const dayIndex = CRM_PICKER_DAY_KEYS.indexOf(dayKey);
      if (dayIndex >= 0 && !disabledWeekDays.includes(dayIndex)) {
        disabledWeekDays.push(dayIndex);
      }
    }
  }

  const rawDayLimit = settings.maxSelectableBookingDays;
  let maxDate;
  if (
    typeof rawDayLimit === "number" &&
    Number.isFinite(rawDayLimit) &&
    rawDayLimit > 0
  ) {
    maxDate = addLocalDays(minDate, Math.floor(rawDayLimit) - 1);
  } else {
    maxDate = addLocalDays(today, CRM_PICKER_DEFAULT_MAX_DAYS_AHEAD);
  }

  if (maxDate.getTime() < minDate.getTime()) {
    maxDate = new Date(minDate.getTime());
  }

  return {
    minDate,
    maxDate,
    disabledWeekDays,
    disabledDates: [],
  };
};

const isDateAllowedByCrmPickerConstraints = (date, constraints) => {
  if (!date || Number.isNaN(date.getTime()) || !constraints) {
    return false;
  }

  const selected = startOfLocalDay(date);
  const minDate = startOfLocalDay(constraints.minDate);
  const maxDate = startOfLocalDay(constraints.maxDate);

  if (selected.getTime() < minDate.getTime()) {
    return false;
  }
  if (selected.getTime() > maxDate.getTime()) {
    return false;
  }

  const disabledWeekDays = Array.isArray(constraints.disabledWeekDays)
    ? constraints.disabledWeekDays
    : [];
  if (disabledWeekDays.includes(selected.getDay())) {
    return false;
  }

  const disabledDates = Array.isArray(constraints.disabledDates)
    ? constraints.disabledDates
    : [];
  if (disabledDates.includes(formatDisabledDateYmd(selected))) {
    return false;
  }

  return true;
};

export {
  HOSPITAL_BOOKING_RULES,
  getMinBookingDate,
  isBookingDateAllowed,
  getCrmDatePickerConstraints,
  isDateAllowedByCrmPickerConstraints,
};
